// @ts-nocheck
import { useState, useEffect, useContext, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate, useGetIdentity, useGetLocale, useSetLocale } from '@refinedev/core';
import { type RefineThemedLayoutHeaderProps, HamburgerMenu } from '@refinedev/mui';
import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import i18n from '../../i18n';
import { languageAnalytics } from '../../utils/analytics';
import type { IIdentity } from '../../interfaces';
import type { Property } from '../../interfaces/property';
import { apiJson, apiVersion } from '../../utils/api';

interface IOptions {
    label: string;
    avatar?: ReactNode;
    link: string;
    category: string;
}

export const Header: React.FC<RefineThemedLayoutHeaderProps> = () => {
    const [value, setValue] = useState('');
    const [options, setOptions] = useState<IOptions[]>([]);
    const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
    const navigate = useNavigate();
    const changeLanguage = useSetLocale();
    const locale = useGetLocale();
    const currentLocale = locale();
    const baseLocale = (currentLocale || 'en').split('-')[0];
    const { data: user } = useGetIdentity<IIdentity | null>();

    const t = useTranslate();

    const fetchData = async (searchTerm: string = '') => {
        try {
            // Build URL with search parameter only if searchTerm is provided
            const propertiesUrl = searchTerm 
                ? `admin/${apiVersion}/properties?search=${encodeURIComponent(searchTerm)}&limit=5`
                : `admin/${apiVersion}/properties?limit=5`;
            
            const placesUrl = searchTerm 
                ? `admin/${apiVersion}/places?search=${encodeURIComponent(searchTerm)}&limit=5`
                : `admin/${apiVersion}/places?limit=5`;

            // Fetch properties
            const propertiesResponse = await apiJson<{ data: Property[]; pagination: any }>(propertiesUrl);
            
            const propertyOptions: IOptions[] = (propertiesResponse.data || []).map(item => {
                return {
                    label: `${item.name}`,
                    link: `/properties/${item.uid}`,
                    category: 'Properties',
                    avatar: (
                        <Avatar
                            variant="rounded"
                            sx={{
                                width: '32px',
                                height: '32px'
                            }}
                            src={item.photos?.[0]?.url}
                        />
                    )
                };
            });

            // Fetch places
            const placesResponse = await apiJson<{ data: any[]; pagination: any }>(placesUrl);
            
            const placeOptions: IOptions[] = (placesResponse.data || []).map((item: any) => {
                const placeName = item.location.name || 'Unnamed Place';
                const address = item.location.address || {};
                const addressText = [address.road, address.city, address.country].filter(Boolean).join(', ') || '';
                return {
                    label: `${placeName}${addressText ? ' - ' + addressText : ''}`,
                    link: `/places`,
                    category: 'Places',
                    avatar: (
                        <Avatar
                            variant="rounded"
                            sx={{
                                width: '32px',
                                height: '32px'
                            }}
                            src={item.location.photos?.[0]?.url}
                        />
                    )
                };
            });

            const newOptions = [...propertyOptions, ...placeOptions];
            
            // Only update options if there are results
            if (newOptions.length > 0) {
                setOptions(newOptions);
            }
        } catch (error) {
            console.error('Search error:', error);
        }
    };

    const handleFocus = () => {
        // Load initial data on first focus if not already loaded
        if (!hasLoadedInitial) {
            setHasLoadedInitial(true);
            fetchData();
        }
    };

    useEffect(() => {
        // Only search if value has at least 3 characters
        if (value.length < 3 && value.length > 0) {
            return;
        }

        // Debounce the search with 300ms delay
        const timeoutId = setTimeout(() => {
            fetchData(value);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [value]);

    return (
        <AppBar
            color="default"
            position="sticky"
            elevation={0}
            sx={{
                '& .MuiToolbar-root': {
                    minHeight: '64px'
                },
                height: '64px',
                borderBottom: theme => `1px solid ${theme.palette.divider}`,
                backgroundColor: theme => theme.palette.background.paper
            }}
        >
            <Toolbar
                sx={{
                    paddingLeft: {
                        xs: '0',
                        sm: '16px',
                        md: '24px'
                    }
                }}
            >
                <Box
                    minWidth="40px"
                    minHeight="40px"
                    marginRight={{
                        xs: '0',
                        sm: '16px'
                    }}
                    sx={{
                        '& .MuiButtonBase-root': {
                            marginLeft: 0,
                            marginRight: 0
                        }
                    }}
                >
                    <HamburgerMenu />
                </Box>

                <Stack
                    direction="row"
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={{
                        xs: '8px',
                        sm: '24px'
                    }}
                >
                    <Stack direction="row" flex={1}>
                        <Autocomplete
                            sx={{
                                maxWidth: 550
                            }}
                            id="search-autocomplete"
                            options={options}
                            filterOptions={x => x}
                            disableClearable
                            freeSolo
                            fullWidth
                            size="small"
                            onInputChange={(event, value) => {
                                if (event?.type === 'change') {
                                    setValue(value);
                                }
                            }}
                            groupBy={option => option.category}
                            renderOption={(props, option: IOptions) => {
                                return (
                                    <Link href={option.link} underline="none">
                                        <Box
                                            {...props}
                                            component="li"
                                            sx={{
                                                display: 'flex',
                                                padding: '10px',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}
                                        >
                                            {option?.avatar && option.avatar}
                                            <Typography
                                                sx={{
                                                    fontSize: {
                                                        md: '14px',
                                                        lg: '16px'
                                                    }
                                                }}
                                            >
                                                {option.label}
                                            </Typography>
                                        </Box>
                                    </Link>
                                );
                            }}
                            renderInput={params => {
                                return (
                                    <Box
                                        position="relative"
                                        sx={{
                                            '& .MuiFormLabel-root': {
                                                paddingRight: '24px'
                                            },
                                            display: {
                                                xs: 'none',
                                                sm: 'block'
                                            }
                                        }}
                                    >
                                        <TextField
                                            {...params}
                                            label={t('search.placeholder')}
                                            onFocus={handleFocus}
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps
                                                }
                                            }}
                                        />
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                right: 0,
                                                top: 0,
                                                '&:hover': {
                                                    backgroundColor: 'transparent'
                                                }
                                            }}
                                        >
                                            <SearchOutlined />
                                        </IconButton>
                                    </Box>
                                );
                            }}
                        />
                    </Stack>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={{
                            xs: '8px',
                            sm: '24px'
                        }}
                    >
                        <Select
                            size="small"
                            disableUnderline
                            value={baseLocale}
                            slotProps={{
                                input: {
                                    'aria-label': 'Without label'
                                }
                            }}
                            variant="outlined"
                            onChange={async (e) => {
                                const lang = e.target.value as string;
                                const previousLanguage = baseLocale || 'en';

                                const detectedLanguage = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null;
                                languageAnalytics.trackLanguageManuallySelected(
                                    lang,
                                    previousLanguage,
                                    detectedLanguage ?? undefined
                                );

                                changeLanguage(lang);

                                try {
                                    const userPrefs = languageAnalytics.getUserLanguagePreferences();
                                    if (import.meta.env.DEV) {
                                        console.log('User language preferences:', userPrefs);
                                    }
                                } catch (error) {
                                    console.warn('Could not get user language preferences:', error);
                                }
                            }}
                            sx={{
                                width: {
                                    xs: '120px',
                                    sm: '160px'
                                }
                            }}
                        >
                            {['en', 'es', 'de', 'nl'].map((lang: string) => (
                                <MenuItem
                                    key={lang}
                                    value={lang}
                                >
                                    <Typography color="text.secondary">
                                        {(() => {
                                            switch(lang) {
                                                case 'en': return 'English';
                                                case 'es': return 'Español';
                                                case 'de': return 'Deutsch';
                                                case 'nl': return 'Nederlands';
                                                default: return lang.toUpperCase();
                                            }
                                        })()}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>

                        {/* Theme toggle button removed - dark mode only */}

                        <Stack
                            direction="column"
                            alignItems="flex-start"
                            justifyContent="center"
                            spacing="2px"
                            sx={{
                                display: {
                                    xs: 'none',
                                    sm: 'flex'
                                },
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 0.8
                                }
                            }}
                            onClick={() => navigate('/profile')}
                        >
                            <Stack
                                direction="row"
                                gap={{
                                    xs: '8px',
                                    sm: '16px'
                                }}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Typography
                                    fontSize={{
                                        xs: '12px',
                                        sm: '14px'
                                    }}
                                    variant="h6"
                                >
                                    {user?.name}
                                </Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                gap={{
                                    xs: '8px',
                                    sm: '16px'
                                }}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Typography
                                    fontSize={{
                                        xs: '12px',
                                        sm: '14px'
                                    }}
                                    variant="h1"
                                >
                                    @{user?.id}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Stack
                            direction="column"
                            alignItems="flex-start"
                            justifyContent="center"
                            flex={1}
                            spacing="2px"
                            sx={{
                                display: {
                                    xs: 'none',
                                    sm: 'flex'
                                },
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 0.8
                                }
                            }}
                            onClick={() => navigate('/profile')}
                        >
                            <Avatar src={user?.photo?.url} alt={user?.name} />
                        </Stack>
                    </Stack>
                </Stack>
            </Toolbar>
        </AppBar>
    );
};
